import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';

import {
  assignGoalToCriticalElement,
  getCriticalElementGoals,
  unassignGoalFromCriticalElement,
} from 'api/generated/critical-element-goals/critical-element-goals';
import { getClientGoals } from 'api/generated/client-goals/client-goals';
import type { ClientGoalDto } from 'api/generated/schemas';

import { useCurrentUser } from 'hooks/api/use-current-user';

interface CurrentUserShape {
  data?: { oktaUserId?: string };
}

interface PendingGoalChange {
  goal: ClientGoalDto;
  action: 'link' | 'unlink';
}

type PendingGoalChanges = Record<number, PendingGoalChange>;

interface UseRelatedGoalsParams {
  clientId: string | number | undefined;
  criticalElementId: string | number | undefined;
  enteringClassYear: string | number | undefined;
  isEditMode: boolean;
  onSave?: MutableRefObject<
    ((newId?: string | number) => Promise<void>) | undefined
  > | null;
}

interface UseRelatedGoalsResult {
  filterOption: 'linked' | 'all';
  handleFilterChange: (option: 'linked' | 'all') => void;
  currentData: ClientGoalDto[];
  linkedGoalsIds: Array<string | number>;
  isCreation: boolean;
  showLoader: boolean;
  isEditMode: boolean;
  handleToggleChange: (goal: ClientGoalDto, newToggleValue: boolean) => void;
  isToggling: boolean;
}

export const useRelatedGoals = ({
  clientId,
  criticalElementId,
  enteringClassYear,
  isEditMode,
  onSave,
}: UseRelatedGoalsParams): UseRelatedGoalsResult => {
  const currentUser = useCurrentUser() as CurrentUserShape;
  const userId = currentUser?.data?.oktaUserId;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isToggling, setIsToggling] = useState<boolean>(false);

  const [linkedData, setLinkedData] = useState<ClientGoalDto[]>([]);
  const [allData, setAllData] = useState<ClientGoalDto[]>([]);
  const [initialLinkedGoalsIds, setInitialLinkedGoalsIds] = useState<
    Array<string | number>
  >([]);
  const [pendingGoalChanges, setPendingGoalChanges] =
    useState<PendingGoalChanges>({});

  const goalsLoadedRef = useRef<boolean>(false);
  const linkedGoalsLoadedRef = useRef<boolean>(false);
  const initialLoadCompletedRef = useRef<boolean>(false);

  const [filterOption, setFilterOption] = useState<'linked' | 'all'>(
    criticalElementId ? 'linked' : 'all'
  );

  useEffect(() => {
    setFilterOption(criticalElementId ? 'linked' : 'all');
  }, [criticalElementId]);

  const effectiveLinkedGoalIds = useMemo(() => {
    const result = [...initialLinkedGoalsIds];

    Object.values(pendingGoalChanges).forEach(({ goal, action }) => {
      if (goal.id == null) {
        return;
      }

      const index = result.indexOf(goal.id);

      if (action === 'link' && index === -1) {
        result.push(goal.id);
      } else if (action === 'unlink' && index !== -1) {
        result.splice(index, 1);
      }
    });

    return result;
  }, [initialLinkedGoalsIds, pendingGoalChanges]);

  const fetchLinkedGoals = useCallback(async (): Promise<void> => {
    if (!criticalElementId || !userId) {
      setLinkedData([]);
      setInitialLinkedGoalsIds([]);

      return;
    }

    if (linkedGoalsLoadedRef.current) {
      return;
    }

    setIsLoading(true);
    try {
      const linkedGoalsRaw = await getCriticalElementGoals(
        userId,
        Number(criticalElementId)
      );

      const linkedGoals = linkedGoalsRaw ?? [];
      const linkedIds = linkedGoals
        .map((goal) => goal.id)
        .filter((id): id is number => id != null);

      setLinkedData(linkedGoals);
      setInitialLinkedGoalsIds(linkedIds);
      setPendingGoalChanges({});

      linkedGoalsLoadedRef.current = true;
    } catch (error) {
      console.error('Error fetching linked goals', error);
      setLinkedData([]);
      setInitialLinkedGoalsIds([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, criticalElementId]);

  const fetchAllGoals = useCallback(async (): Promise<void> => {
    if (!userId || !clientId) return;

    if (goalsLoadedRef.current) {
      return;
    }

    setIsLoading(true);
    try {
      const yearToFetch = enteringClassYear || new Date().getFullYear();

      const page = await getClientGoals(userId, Number(clientId), {
        'enteringClassYear.equals': Number(yearToFetch),
        size: 1000,
        sort: ['createdDate,desc'],
      });

      const goals = page.content ?? [];

      setAllData(goals);

      goalsLoadedRef.current = true;
    } catch (error) {
      console.error('Error fetching all goals', error);
      setAllData([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, clientId, enteringClassYear]);

  const handleFilterChange = useCallback((option: 'linked' | 'all'): void => {
    setFilterOption(option);
  }, []);

  const handleToggleChange = useCallback(
    (goal: ClientGoalDto, newToggleValue: boolean): void => {
      const goalNumericId = goal.id;

      if (!isEditMode || goalNumericId == null) return;

      setPendingGoalChanges((prev) => {
        const wasInitiallyLinked =
          initialLinkedGoalsIds.includes(goalNumericId);
        const action: 'link' | 'unlink' = newToggleValue ? 'link' : 'unlink';

        if (
          (newToggleValue && wasInitiallyLinked) ||
          (!newToggleValue && !wasInitiallyLinked)
        ) {
          const { [goalNumericId]: _removed, ...rest } = prev;

          return rest;
        }

        return { ...prev, [goalNumericId]: { goal, action } };
      });
    },
    [isEditMode, initialLinkedGoalsIds]
  );

  const applyPendingChanges = useCallback(
    async (newCriticalElementId?: string | number): Promise<void> => {
      const targetId = newCriticalElementId || criticalElementId;

      if (!targetId || !userId) return;

      setIsToggling(true);
      try {
        for (const { goal, action } of Object.values(pendingGoalChanges)) {
          if (goal.id == null) {
            continue;
          }

          if (action === 'link') {
            await assignGoalToCriticalElement(
              userId,
              Number(targetId),
              Number(goal.id)
            );
          } else if (action === 'unlink') {
            await unassignGoalFromCriticalElement(
              userId,
              Number(targetId),
              Number(goal.id)
            );
          }
        }

        setInitialLinkedGoalsIds(effectiveLinkedGoalIds);
        setPendingGoalChanges({});

        if (newCriticalElementId) {
          linkedGoalsLoadedRef.current = false;
          await fetchLinkedGoals();
        }
      } catch (error) {
        console.error('Error applying goal changes', error);
        await fetchLinkedGoals();
      } finally {
        setIsToggling(false);
      }
    },
    [
      userId,
      criticalElementId,
      pendingGoalChanges,
      effectiveLinkedGoalIds,
      fetchLinkedGoals,
    ]
  );

  const currentData = useMemo(() => {
    if (filterOption === 'linked') {
      return (allData.length > 0 ? allData : linkedData).filter(
        (goal) => goal.id != null && effectiveLinkedGoalIds.includes(goal.id)
      );
    }

    return allData;
  }, [filterOption, linkedData, allData, effectiveLinkedGoalIds]);

  useEffect(() => {
    if (onSave) {
      onSave.current = applyPendingChanges;
    }
  }, [onSave, applyPendingChanges]);

  useEffect(() => {
    if (initialLoadCompletedRef.current) {
      return;
    }

    const loadData = async (): Promise<void> => {
      if (!enteringClassYear) return;
      await fetchAllGoals();

      if (criticalElementId) {
        await fetchLinkedGoals();
      }

      initialLoadCompletedRef.current = true;
    };

    loadData();
  }, [enteringClassYear]);

  useEffect(() => {
    goalsLoadedRef.current = false;
    linkedGoalsLoadedRef.current = false;
    initialLoadCompletedRef.current = false;
  }, [clientId, enteringClassYear]);

  useEffect(() => {
    if (criticalElementId) {
      linkedGoalsLoadedRef.current = false;
    }
  }, [criticalElementId]);

  useEffect(() => {
    if (!isEditMode) {
      setPendingGoalChanges({});
    }
  }, [isEditMode]);

  return {
    filterOption,
    handleFilterChange,
    currentData,
    linkedGoalsIds: effectiveLinkedGoalIds,
    isCreation: !criticalElementId && isEditMode,
    showLoader: isLoading || isToggling,
    isEditMode,
    handleToggleChange,
    isToggling,
  };
};

export default useRelatedGoals;
